import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Container,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  QrCodeScanner as QrIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  DirectionsCar as CarIcon,
  Home as HomeIcon,
  Business as BuildingIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import jsQR from 'jsqr';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, CameraDirection } from '@capacitor/camera';
import api, { usersAPI, flatsAPI } from '../services/api';

// Add CSS to force video visibility
const videoStyles = `
  .qr-scanner-video {
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    z-index: 1 !important;
  }
`;

const SecurityGuardQRScanner = () => {
  console.log('🔄 SecurityGuardQRScanner component loading - Version 2.0');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Inject CSS for video visibility
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = videoStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const { user } = useAuth();
  const videoRef = useRef();
  const qrScannerRef = useRef();
  const [isScanning, setIsScanning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [lastScanResult, setLastScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [buildingIds, setBuildingIds] = useState([]);
  const [buildingName, setBuildingName] = useState('Building');
  const [userId, setUserId] = useState(null);
  const [isNativeApp, setIsNativeApp] = useState(false);

  // Detect if running in native app and check permissions
  useEffect(() => {
    const detectEnvironment = async () => {
      const isNative = Capacitor.isNativePlatform();
      setIsNativeApp(isNative);
      console.log('🔍 Environment detected:', isNative ? 'Native Mobile App' : 'Web Browser');
      
      if (isNative) {
        try {
          const permissions = await Camera.checkPermissions();
          console.log('📷 Camera permissions status:', permissions);
          
          if (permissions.camera === 'denied') {
            console.warn('⚠️ Camera permission denied');
          } else if (permissions.camera === 'prompt' || permissions.camera === 'prompt-with-rationale') {
            console.log('📷 Camera permission will be requested when needed');
          }
        } catch (error) {
          console.error('❌ Error checking camera permissions:', error);
        }
      }
    };
    
    detectEnvironment();
  }, []);
  
  // Collect user data and building associations
  useEffect(() => {
    const collectUserData = async () => {
      if (user) {
        // Collect user from the user context and retrieve userId
        const extractedUserId = user.userId || user.attributes?.sub || user.username;
        setUserId(extractedUserId);
        console.log('User ID collected:', extractedUserId);
        
        if (extractedUserId) {
          try {
            // Logic for collecting the buildingId
            const res = await usersAPI.getAssociations(extractedUserId);
            // Collect buildingIds and building name from association results
            const buildingAssociations = res.data || [];
            
            const ids = buildingAssociations.map(item => item.buildingId);
            setBuildingIds(ids);
            
            // Set building name from the first building association
            if (buildingAssociations.length > 0) {
              const firstBuilding = buildingAssociations[0];
              setBuildingName(firstBuilding.buildingName || 'Building');
            }
            console.log('Building Associations:', buildingAssociations);
            console.log('Building IDs:', ids);
            if (ids.length !== 0) {
              const buildingId = ids[0];
              console.log('Primary Building ID:', buildingId);
            }
          } catch (error) {
            console.error('Error fetching user associations:', error);
          }
        }
      }
    };
    
    collectUserData();
  }, [user]);

  // Mobile QR scanning using Capacitor Camera
  const scanWithCapacitorCamera = async () => {
    try {
      setIsLoading(true);
      setScanError(null);
      
      console.log('📱 Starting Capacitor camera for QR scanning...');
      
      // Request camera permissions if needed
      try {
        const permissions = await Camera.requestPermissions({ permissions: ['camera'] });
        if (permissions.camera === 'denied') {
          throw new Error('Camera permission denied. Please enable camera access in app settings.');
        }
      } catch (permError) {
        console.error('❌ Permission error:', permError);
        throw permError;
      }
      
      // Take a photo using Capacitor Camera
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        direction: CameraDirection.Rear,
        saveToGallery: false,
        correctOrientation: true,
        width: 1024,
        height: 1024
      });
      
      console.log('📸 Photo captured via Capacitor');
      
      // Create image element for QR scanning
      const img = new Image();
      img.onload = () => {
        console.log('🖼️ Image loaded for QR scanning');
        
        // Create canvas to extract image data
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        
        // Get image data for jsQR
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // Try to detect QR code
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (qrCode && qrCode.data) {
          console.log('✅ QR code detected from photo:', qrCode.data);
          handleScan(qrCode.data);
        } else {
          console.log('❌ No QR code found in image');
          setScanError('No QR code found in the image. Please try again.');
        }
        
        setIsLoading(false);
      };
      
      img.onerror = () => {
        console.error('❌ Failed to load captured image');
        setScanError('Failed to process captured image. Please try again.');
        setIsLoading(false);
      };
      
      img.src = photo.dataUrl;
      
    } catch (error) {
      console.error('❌ Capacitor camera error:', error);
      let errorMessage = 'Camera access failed. ';
      
      if (error.message?.includes('User cancelled')) {
        errorMessage = 'Camera capture was cancelled.';
      } else if (error.message?.includes('permission')) {
        errorMessage = 'Camera permission denied. Please enable camera access in app settings.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      setScanError(errorMessage);
      setIsLoading(false);
    }
  };
  
  // Initialize QR Scanner
  const initializeScanner = async () => {
    try {
      setIsInitializing(true);
      setScanError(null);
      
      // Ensure video element exists
      if (!videoRef.current) {
        setScanError('Video element not available');
        setIsInitializing(false);
        return;
      }
      
      console.log('Step 1: Video element found, checking camera support...');
      console.log('📱 Environment:', isNativeApp ? 'Native Mobile App' : 'Web Browser');
      
      // For native mobile apps, try to request permissions via Capacitor first (non-blocking)
      if (isNativeApp) {
        try {
          console.log('📱 Requesting camera permissions via Capacitor...');
          const permissions = await Camera.requestPermissions({ permissions: ['camera'] });
          console.log('📱 Capacitor permissions result:', permissions);
          
          if (permissions.camera === 'denied') {
            console.warn('⚠️ Capacitor permissions denied, will try web permissions');
          }
        } catch (permError) {
          console.log('ℹ️ Capacitor permission request failed, continuing with web approach:', permError.message);
        }
      }
      
      // Check for camera permissions on mobile
      if (isMobile && navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({ name: 'camera' });
          if (permission.state === 'denied') {
            setScanError('Camera permission denied. Please enable camera access in browser settings and refresh.');
            setIsInitializing(false);
            return;
          }
        } catch (permError) {
          // Permission API not supported, continue
        }
      }
      
      // Progressive camera access with mobile-optimized constraints
      let stream;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isNativeApp;
      
      try {
        // Mobile-optimized constraints for better QR scanning performance
        let constraints;
        
        if (isMobileDevice || isNativeApp) {
          console.log('📱 Using mobile-optimized camera constraints');
          constraints = {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 },
              frameRate: { ideal: 30, max: 30 },
              focusMode: { ideal: 'continuous' },
              exposureMode: { ideal: 'continuous' },
              whiteBalanceMode: { ideal: 'continuous' }
            }
          };
        } else {
          console.log('🖥️ Using desktop camera constraints');
          constraints = {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 }
            }
          };
        }
        
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('📱 Step 2: Camera access successful with optimized constraints');
        } catch (primaryError) {
          console.log('📱 Step 2a: Optimized constraints failed, trying basic constraints');
          console.log('Primary error:', primaryError.message);
          
          // Fallback to most basic constraints for mobile
          const basicConstraints = {
            video: {
              facingMode: 'environment'
            }
          };
          
          try {
            stream = await navigator.mediaDevices.getUserMedia(basicConstraints);
            console.log('📱 Step 2b: Camera access successful with basic constraints');
          } catch (basicError) {
            console.log('📱 Step 2c: Basic constraints failed, trying any camera');
            console.log('Basic error:', basicError.message);
            
            // Last resort: try any available camera
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
            console.log('📱 Step 2d: Camera access successful with any camera');
          }
        }
        
        // Set the stream to video element with mobile optimization
        console.log('📹 Setting up video element for mobile...');
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        videoRef.current.autoplay = true;
        
        // Add mobile-specific attributes
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        if (isMobileDevice || isNativeApp) {
          videoRef.current.setAttribute('x5-playsinline', 'true');
          videoRef.current.setAttribute('x5-video-player-type', 'h5');
          videoRef.current.setAttribute('x-webkit-airplay', 'allow');
        }
        
        // Force video display styles for mobile
        videoRef.current.style.display = 'block';
        videoRef.current.style.width = '100%';
        videoRef.current.style.height = '100%';
        videoRef.current.style.objectFit = 'cover';
        
        console.log('📹 Video element configured, stream tracks:', stream.getTracks().length);
        
        // Enhanced video event listeners for mobile debugging
        videoRef.current.onloadedmetadata = () => {
          console.log('✅ Video metadata loaded:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
          console.log('📹 Video element debug:');
          console.log('  └─ readyState:', videoRef.current.readyState);
          console.log('  └─ videoWidth:', videoRef.current.videoWidth);
          console.log('  └─ videoHeight:', videoRef.current.videoHeight);
          console.log('  └─ srcObject type:', typeof videoRef.current.srcObject);
          console.log('  └─ srcObject tracks:', videoRef.current.srcObject?.getTracks?.()?.length || 'unknown');
          console.log('  └─ display style:', videoRef.current.style.display);
          console.log('  └─ visibility:', getComputedStyle(videoRef.current).visibility);
        };
        
        videoRef.current.onplay = () => {
          console.log('▶️ Video started playing successfully');
        };
        
        videoRef.current.oncanplay = () => {
          console.log('🎬 Video can start playing - ready state reached');
        };
        
        videoRef.current.onloadstart = () => {
          console.log('📥 Video load started');
        };
        
        videoRef.current.onerror = (error) => {
          console.error('❌ Video error occurred:', error);
          console.error('Video error details:', {
            code: videoRef.current.error?.code,
            message: videoRef.current.error?.message
          });
        };
        
        // Try to play video
        console.log('🎬 Attempting to play video...');
        try {
          await videoRef.current.play();
          console.log('✅ Step 3: Video playing successfully');
        } catch (playError) {
          console.warn('⚠️ Video play failed, but continuing:', playError.message);
          // Don't throw error here, some mobile browsers are quirky about autoplay
        }
        
        // Wait for video to be ready with enhanced mobile checking
        await new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50; // Extended timeout for mobile: 5 seconds
          
          const checkVideo = () => {
            attempts++;
            console.log(`🔍 Video ready check attempt ${attempts}/${maxAttempts}`);
            
            const videoReady = videoRef.current && 
                             videoRef.current.videoWidth > 0 && 
                             videoRef.current.videoHeight > 0 &&
                             videoRef.current.readyState >= 2; // HAVE_CURRENT_DATA or higher
            
            if (videoReady) {
              console.log('✅ Step 4: Video ready! Dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
              console.log('✅ Video readyState:', videoRef.current.readyState);
              resolve();
            } else if (attempts >= maxAttempts) {
              console.error('❌ Video failed to initialize after', maxAttempts * 100, 'ms');
              console.error('❌ Video state at timeout:');
              if (videoRef.current) {
                console.error('  └─ videoWidth:', videoRef.current.videoWidth);
                console.error('  └─ videoHeight:', videoRef.current.videoHeight);
                console.error('  └─ readyState:', videoRef.current.readyState);
                console.error('  └─ paused:', videoRef.current.paused);
                console.error('  └─ ended:', videoRef.current.ended);
                console.error('  └─ srcObject:', !!videoRef.current.srcObject);
              }
              reject(new Error('Video initialization timeout - camera may not be accessible'));
            } else {
              console.log(`⏳ Waiting for video... videoWidth: ${videoRef.current?.videoWidth || 0}, readyState: ${videoRef.current?.readyState || 0}`);
              setTimeout(checkVideo, 100);
            }
          };
          checkVideo();
        });
        
        // Now initialize QR scanner with mobile-optimized settings
        qrScannerRef.current = new QrScanner(
          videoRef.current,
          (result) => {
            console.log('🎯 === QR SCAN DETECTED ===');
            console.log('🎯 Result object:', result);
            console.log('🎯 Result type:', typeof result);
            console.log('🎯 Result.data:', result.data);
            console.log('🎯 Direct result:', result);
            
            const qrData = result.data || result;
            console.log('🎯 Extracted QR data:', qrData);
            console.log('🎯 QR data type:', typeof qrData);
            console.log('🎯 QR data length:', qrData?.length);
            console.log('🎯 Current isLoading state:', isLoading);
            
            if (qrData && !isLoading) {
              console.log('🚀 ✅ Triggering handleScan with:', qrData);
              handleScan(qrData);
            } else if (isLoading) {
              console.log('⏳ ❌ Scan detected but blocked due to loading state');
            } else {
              console.log('❌ No QR data extracted from result');
            }
          },
          {
            onDecodeError: (error) => {
              // Only log significant errors, not the constant "no QR code found"
              if (error && error.message && 
                  !error.message.toLowerCase().includes('no qr code found') && 
                  !error.message.toLowerCase().includes('no multiformat readers') &&
                  !error.message.toLowerCase().includes('no barcode found')) {
                console.log('QR decode error:', error.message);
              }
            },
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: isMobileDevice ? 5 : 10, // Lower scan rate for mobile performance
            returnDetailedScanResult: false,
            preferredCamera: 'environment',
            // Mobile-specific optimizations
            calculateScanRegion: isMobileDevice ? (video) => {
              // Focus on center area for mobile to improve detection
              const smallerDimension = Math.min(video.videoWidth, video.videoHeight);
              const scanSize = Math.round(smallerDimension * 0.6);
              const x = Math.round((video.videoWidth - scanSize) / 2);
              const y = Math.round((video.videoHeight - scanSize) / 2);
              return {
                x: x,
                y: y,
                width: scanSize,
                height: scanSize,
                downScaledWidth: scanSize,
                downScaledHeight: scanSize
              };
            } : undefined
          }
        );
        
        console.log('Step 5: QR scanner created, now starting scanner');
        
        // Start the QR scanner
        try {
          await qrScannerRef.current.start();
          console.log('Step 6: QR scanner started and ready for scanning');
        } catch (scannerError) {
          console.warn('QR scanner start failed (HTTPS may be required):', scannerError.message);
          // Continue anyway - fallback scanning may still work
        }
        
        // Force scanner to start scanning with the current video
        if (qrScannerRef.current && videoRef.current.videoWidth > 0) {
          try {
            await qrScannerRef.current.start();
            console.log('Step 7: QR scanner started and ready for auto-detection');
          } catch (scannerError) {
            console.warn('⚠️ QR Scanner failed to start (likely HTTPS required):', scannerError.message);
            console.log('🔧 Falling back to manual scanning only');
          }
          
          // Ensure video element remains visible after QR scanner starts
          if (videoRef.current) {
            console.log('🔧 Forcing video element visibility after QR scanner start');
            videoRef.current.style.display = 'block';
            videoRef.current.style.visibility = 'visible';
            videoRef.current.style.opacity = '1';
            videoRef.current.style.position = 'relative';
            videoRef.current.style.zIndex = '1';
            
            // Check if video is actually visible
            const computedStyle = window.getComputedStyle(videoRef.current);
            console.log('📺 Video computed styles:');
            console.log('  └─ display:', computedStyle.display);
            console.log('  └─ visibility:', computedStyle.visibility);
            console.log('  └─ opacity:', computedStyle.opacity);
            console.log('  └─ width:', computedStyle.width);
            console.log('  └─ height:', computedStyle.height);
          }
          
          // Enhanced manual scan polling for mobile and HTTP development
          if (window.location.protocol === 'http:' || isMobileDevice) {
            const reasonText = window.location.protocol === 'http:' ? 'HTTP development' : 'mobile optimization';
            console.log(`🚀 ${reasonText} - enabling enhanced manual scan polling`);
            
            // Create canvas for image capture
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d', { willReadFrequently: true });
            
            const manualScanInterval = setInterval(async () => {
              if (videoRef.current && !isLoading && videoRef.current.videoWidth > 0) {
                try {
                  // Capture frame from video
                  canvas.width = videoRef.current.videoWidth;
                  canvas.height = videoRef.current.videoHeight;
                  context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                  
                  // Get image data for jsQR
                  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                  
                  // Try jsQR detection with mobile optimizations
                  const qrCodeResult = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'attemptBoth'
                  });
                  
                  if (qrCodeResult && qrCodeResult.data) {
                    console.log('🎯✅ jsQR detected QR code:', qrCodeResult.data);
                    clearInterval(manualScanInterval);
                    handleScan(qrCodeResult.data);
                    return;
                  }
                  
                  // Fallback: try QR scanner's decode if engine exists
                  if (qrScannerRef.current && qrScannerRef.current._qrEngine) {
                    const result = await qrScannerRef.current._qrEngine.decode(videoRef.current);
                    if (result && result.data) {
                      console.log('🎯✅ QR scanner fallback detected:', result.data);
                      clearInterval(manualScanInterval);
                      handleScan(result.data);
                      return;
                    }
                  }
                  
                } catch (error) {
                  // Silent catch - no QR code found this frame
                }
              }
            }, isMobileDevice ? 800 : 500); // Slower for mobile to save battery
            
            // Clean up interval after 300 seconds (5 minutes)
            setTimeout(() => {
              clearInterval(manualScanInterval);
              console.log('⏰ Manual scan polling stopped after 5 minutes');
            }, 300000);
            
            console.log(`🎬 Continuous QR scanning active (${reasonText}) - point camera at QR code`);
          }
        }
        
        // Add debug logging to see if scanner is active
        let scanAttempts = 0;
        const debugInterval = setInterval(() => {
          scanAttempts++;
          const scannerState = qrScannerRef.current ? 'active' : 'inactive';
          const videoPlaying = videoRef.current && !videoRef.current.paused;
          const videoSrc = videoRef.current?.srcObject ? 'blob-stream' : 'no-stream';
          const videoDimensions = videoRef.current ? `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}` : 'unknown';
          
          console.log(`🔍 Auto-Scan Status ${scanAttempts}:`);
          console.log(`  └─ Scanner: ${scannerState}`);
          console.log(`  └─ Video: ${videoPlaying ? 'playing' : 'paused'} (${videoSrc})`);
          console.log(`  └─ Dimensions: ${videoDimensions}`);
          console.log(`  └─ Loading: ${isLoading}`);
          console.log(`  └─ Stream URL: ${videoRef.current?.srcObject ? 'Active blob stream' : 'No stream'}`);
          
          // Test if scanner is actually scanning by checking internal state
          if (qrScannerRef.current) {
            console.log(`  └─ Scanner._active: ${qrScannerRef.current._active}`);
            console.log(`  └─ Scanner._qrEngine: ${!!qrScannerRef.current._qrEngine}`);
            console.log(`  └─ Scanner._video: ${!!qrScannerRef.current._video}`);
          }
          
          // Stop debug after 10 attempts (30 seconds)
          if (scanAttempts >= 10) {
            clearInterval(debugInterval);
            console.log('🔍 Auto-scan debug logging stopped. Scanner should be detecting QR codes automatically.');
            console.log('📱 Hold a QR code in front of the camera - it should detect automatically!');
            
            // Final scanner health check
            if (qrScannerRef.current) {
              console.log('🏥 Final Scanner Health Check:');
              console.log('   Active:', qrScannerRef.current._active);
              console.log('   Has Camera:', !!qrScannerRef.current._video);
              console.log('   Has Engine:', !!qrScannerRef.current._qrEngine);
            }
          }
        }, 3000);
        
        // Update UI state only after successful initialization
        setIsScanning(true);
        setIsInitializing(false);
        setScanError(null);
        
        console.log('✅ QR Scanner is now ready for scanning. Point camera at QR code.');
        console.log('📱 Mobile scanner fully initialized and ready!');
        
      } catch (cameraError) {
        console.error('❌ Camera access error:', cameraError);
        
        // Clean up stream if it was created
        if (stream) {
          console.log('🧹 Cleaning up camera stream due to error');
          stream.getTracks().forEach(track => {
            console.log('🛑 Stopping track:', track.kind);
            track.stop();
          });
        }
        
        throw cameraError;
      }
      
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      let errorMessage = 'Failed to access camera. ';
      
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please grant camera permissions and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Camera not supported in this browser.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Camera is being used by another application.';
      } else {
        errorMessage += `Error: ${error.message || 'Unknown error occurred'}`;
      }
      
      setScanError(errorMessage);
      setIsScanning(false);
      setIsInitializing(false);
      
      // Clean up video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  // Stop QR Scanner
  const stopScanner = () => {
    console.log('Stopping QR scanner...');
    
    // Stop QR scanner
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    
    // Stop camera stream
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      if (stream.getTracks) {
        stream.getTracks().forEach(track => {
          console.log('Stopping track:', track.kind);
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
    setIsInitializing(false);
  };

  // Handle QR code scan
  const handleScan = async (qrData) => {
    if (isLoading || !qrData) {
      console.log('🚫 Scan blocked - isLoading:', isLoading, 'qrData:', qrData);
      return;
    }

    console.log('🔍 QR Code scanned:', qrData);
    console.log('🔍 Raw QR data type:', typeof qrData);
    console.log('🔍 Raw QR data length:', qrData.length);
    
    // Clear all previous states
    setIsLoading(true);
    setScanError(null);
    setValidationResult(null); // Clear previous validation result
    setShowResultDialog(false); // Ensure dialog is closed
    setLastScanResult(qrData);

    try {
      // Stop scanner temporarily to prevent multiple scans
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        setIsScanning(false);
      }

      // Validate QR code with backend
      console.log('🚀 Sending QR for validation:', qrData);
      const response = await validateQRCode(qrData);
      console.log('✅ Full validation response:', JSON.stringify(response.data, null, 2));
      console.log('✅ Response status:', response.status);
      console.log('✅ Response.data.isValid:', response.data.isValid);
      console.log('✅ Response.data.success:', response.data.success);
      console.log('✅ Response.data.message:', response.data.message);
      
      // Handle both successful API calls and validation failures
      if (response.data.isValid === false || response.data.success === false) {
        console.log('⚠️ Vehicle validation failed:', response.data);
        // Vehicle not found or invalid - show error dialog
        setValidationResult({
          isValid: false,
          success: false,
          vehicleNumber: response.data.vehicleNumber || qrData,
          message: response.data.message || `Vehicle ${response.data.vehicleNumber || qrData} not found in system`,
          errorType: response.data.errorType || 'VALIDATION_ERROR'
        });
      } else if (response.data.isValid === true || response.data.success === true) {
        console.log('✅ Vehicle validation successful:', response.data);
        // Vehicle found and valid - show success
        setValidationResult({
          isValid: true,
          success: true,
          message: response.data.message || 'Vehicle validated successfully',
          vehicleNumber: response.data.vehicleNumber || qrData,
          vehicleDetails: response.data.vehicleDetails,
          flatDetails: response.data.flatDetails
        });
      } else {
        console.log('⚠️ Unclear validation response:', response.data);
        // Fallback for unclear responses
        setValidationResult({
          isValid: false,
          success: false,
          vehicleNumber: qrData,
          message: 'Validation response unclear - please try again',
          errorType: 'UNCLEAR_RESPONSE'
        });
      }
      
      setShowResultDialog(true);
      
    } catch (error) {
      console.error('❌ QR validation error:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      
      let errorMessage = 'Failed to validate QR code. Please try again.';
      let vehicleNumber = qrData;
      
      if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
        vehicleNumber = error.response.data.vehicleNumber || qrData;
      }
      
      setScanError(errorMessage);
      setValidationResult({
        isValid: false,
        success: false,
        vehicleNumber: vehicleNumber,
        message: errorMessage,
        error: true,
        errorType: 'API_ERROR'
      });
      setShowResultDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate QR code with backend API
  const validateQRCode = async (qrCode) => {
    console.log('🔍 === QR VALIDATION START ===');
    console.log('🔍 Raw QR code:', qrCode);
    console.log('🔍 User ID:', userId);
    console.log('🔍 Building IDs:', buildingIds);
    console.log('🔍 Primary Building ID:', buildingIds[0]);
    
    if (!userId) {
      console.error('❌ No user ID found!');
      throw new Error('User authentication required. Please log in again.');
    }
    
    if (buildingIds.length === 0) {
      console.error('❌ No building associations found!');
      throw new Error('No building access found. Please contact administrator.');
    }
    
    // Handle both old and new QR formats
    let vehicleNumber = qrCode.trim().toUpperCase();
    
    // Check if it's the old structured format: VEHICLE_QR:VEHICLE_NUMBER:FLAT_ID
    if (qrCode.startsWith('VEHICLE_QR:')) {
      const parts = qrCode.split(':');
      if (parts.length >= 2) {
        vehicleNumber = parts[1].trim().toUpperCase();
        console.log('🔄 Detected old QR format, extracted vehicle number:', vehicleNumber);
      }
    } else {
      console.log('🔄 Detected new QR format (direct vehicle number):', vehicleNumber);
    }
    
    console.log('🚗 Final vehicle number for validation:', vehicleNumber);
    console.log('🚗 Expected DB lookup:', `VEHICLENO#${vehicleNumber}`);
    
    try {
      // Use flatsAPI with the primary building ID
      const primaryBuildingId = buildingIds[0];
      console.log('🏢 Validating with building ID:', primaryBuildingId);
      
      const response = await flatsAPI.validateQRCode(vehicleNumber, primaryBuildingId);
      console.log('✅ API Response received:', response.status, typeof response.data === 'string' ? 'HTML Response' : response.data);
      
      // Check if response is HTML (indicating API gateway/routing issue)
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        console.error('❌ Received HTML response instead of JSON - API routing issue');
        throw new Error('API endpoint not found. Please check API Gateway configuration.');
      }
      
      console.log('✅ Full response object:', JSON.stringify(response.data, null, 2));
      
      // Check if the response indicates success or failure
      if (response.data && response.data.isValid === false) {
        console.log('⚠️ Vehicle validation failed:', response.data.message);
        // Return the failed validation result
        return {
          ...response,
          data: {
            ...response.data,
            success: false
          }
        };
      }
      
      console.log('✅ Vehicle validation successful');
      return response;
    } catch (error) {
      console.error('❌ QR validation failed:');
      console.error('   Status:', error.response?.status);
      console.error('   Status Text:', error.response?.statusText);
      console.error('   Response Data:', error.response?.data);
      console.error('   URL:', error.config?.url);
      console.error('   Headers:', error.config?.headers);
      throw error;
    }
  };



  // Close result dialog and stop camera
  const handleCloseResultDialog = () => {
    console.log('🚪 Closing result dialog and stopping camera');
    setShowResultDialog(false);
    setValidationResult(null);
    setScanError(null);
    setIsLoading(false);
    
    // Stop the camera completely when dialog closes
    stopScanner();
  };



  // Component lifecycle
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        align="center" 
        color="primary"
        sx={{
          fontSize: { xs: '1.5rem', md: '2rem' },
          mb: { xs: 2, md: 3 }
        }}
      >
        Security Guard - Vehicle QR Scanner
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* QR Scanner Section */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, height: 'fit-content' }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              QR Code Scanner
            </Typography>

            {!isScanning && !isLoading && !isInitializing && (
              <Box textAlign="center" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<QrIcon />}
                  onClick={initializeScanner}
                  size={isMobile ? "medium" : "large"}
                  fullWidth={isMobile}
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    py: { xs: 1, md: 1.5 },
                    mb: 1
                  }}
                >
                  {isNativeApp ? 'Capture QR Code' : 'Start Scanning'}
                </Button>
              </Box>
            )}

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: '250px', md: '300px' },
                bgcolor: '#f5f5f5',
                border: '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              <video
                ref={videoRef}
                className="qr-scanner-video"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: isScanning ? 'block' : 'none',
                  backgroundColor: '#000',
                  borderRadius: '8px'
                }}
                muted
                playsInline
                autoPlay
                webkit-playsinline="true"
                x5-playsinline="true"
                controls={false}
              />
              
              {!isScanning && !isLoading && !isInitializing && (
                <Box textAlign="center">
                  <QrIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    {isNativeApp 
                      ? 'Click "Capture QR Code" to take a photo' 
                      : 'Click "Start Scanning" to begin'}
                  </Typography>
                </Box>
              )}

              {isInitializing && (
                <Box textAlign="center">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Initializing Camera...
                  </Typography>
                </Box>
              )}

              {isLoading && (
                <Box textAlign="center">
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Validating QR Code...
                  </Typography>
                </Box>
              )}
            </Box>

            {isScanning && (
              <Box mt={2} textAlign="center" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isNativeApp && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      📱 Mobile live scanning - hold QR code steady in camera view
                    </Typography>
                  </Alert>
                )}
                {!isNativeApp && window.location.protocol === 'http:' && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      🔄 Development mode - continuous scanning active
                    </Typography>
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  onClick={stopScanner}
                  color="secondary"
                  fullWidth={isMobile}
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    py: { xs: 1, md: 1.5 }
                  }}
                >
                  Stop Scanning
                </Button>
              </Box>
            )}

            {scanError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {scanError}
              </Alert>
            )}

            {lastScanResult && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Last scanned: {lastScanResult}
                </Typography>
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Validation Result Dialog */}
      <Dialog
        open={showResultDialog}
        onClose={handleCloseResultDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, md: 2 },
            width: { xs: 'calc(100% - 16px)', md: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            {validationResult?.isValid ? (
              <SuccessIcon color="success" />
            ) : (
              <ErrorIcon color="error" />
            )}
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
            >
              {validationResult?.isValid 
                ? 'Valid Vehicle'
                : 'Invalid Vehicle'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert 
            severity={validationResult?.isValid ? 'success' : 'error'}
            sx={{ mb: 2 }}
          >
            {validationResult?.message || 'Unknown error occurred'}
          </Alert>

          {/* Show vehicle number even for failed validations */}
          {!validationResult?.isValid && validationResult?.vehicleNumber && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Scanned Vehicle Details
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CarIcon color="disabled" />
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                      Vehicle Not Found
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    <strong>Scanned Number:</strong> {validationResult.vehicleNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> Not registered in the system
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {validationResult?.isValid && validationResult?.vehicleDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Vehicle Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CarIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Vehicle
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>Number:</strong> {validationResult.vehicleDetails.vehicleNumber || lastScanResult}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {validationResult.vehicleDetails.vehicleType}
                      </Typography>
                      {validationResult.vehicleDetails.vehicleBrand && (
                        <Typography variant="body2">
                          <strong>Brand:</strong> {validationResult.vehicleDetails.vehicleBrand}
                        </Typography>
                      )}
                      {validationResult.vehicleDetails.vehicleModel && (
                        <Typography variant="body2">
                          <strong>Model:</strong> {validationResult.vehicleDetails.vehicleModel}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <HomeIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Flat Details
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        <strong>Flat:</strong> {validationResult.flatDetails.flatNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Building:</strong> {validationResult.flatDetails.buildingId}
                      </Typography>
                      {validationResult.flatDetails.blockName && (
                        <Typography variant="body2">
                          <strong>Block:</strong> {validationResult.flatDetails.blockName}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {validationResult?.logEntry && (
                <Box mt={2}>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" alignItems="center" gap={1}>
                    <TimeIcon color="success" />
                    <Typography variant="body2" color="success.main">
                      Entry logged successfully at {new Date(validationResult.logEntry.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, md: 1 }, 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1, md: 0 }
        }}>
          <Button onClick={handleCloseResultDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SecurityGuardQRScanner;